/**
 * The "Quantity" pill from the Canvas UI 2.0 frames — credit estimate and run
 * count in ONE rounded container, split by a divider.
 *
 * The frames (Figma `137:1638`) draw cost and count as a single 31px pill
 * sitting in the same row as the AI Model and Style pills. Both the Config and
 * Refine control bars previously rendered the cost as a bare `<span>` floating
 * beside a separate count pill, which read as two unrelated controls.
 *
 * Colours come from the app's own tokens (`surface-2` / `border-subtle`) rather
 * than the frame's literal hexes, so this matches the Model and Style pills
 * already in the row and follows the theme.
 *
 * Presentational only: the count segment is passed as `children` so each
 * parent keeps its own popover state, refs and test ids.
 */

import type { ReactNode } from 'react'

export interface CreditQuantityPillProps {
  /** Credits for one run × the count. `undefined` renders an em dash — the
   *  model has no published rate (BYOM), which is not the same as "free". */
  sparks: number | undefined
  /** Test id for the credit segment; differs per parent for historical reasons. */
  sparksTestId: string
  /** The count selector. Omitted for nodes whose backend ignores the count. */
  children?: ReactNode
}

export function CreditQuantityPill({ sparks, sparksTestId, children }: CreditQuantityPillProps) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface-2 px-2.5 py-1 text-xs font-medium text-text-muted"
      data-testid="credit-quantity-pill"
    >
      <span className="flex items-center gap-1 tabular-nums" data-testid={sparksTestId}>
        <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
        </svg>
        {sparks ?? '—'}
      </span>
      {children ? (
        <>
          {/* Full-bleed divider: `-my-1` cancels the pill's vertical padding so
              the rule spans the whole height, as the frame draws it. */}
          <span aria-hidden className="-my-1 w-px self-stretch bg-border-subtle" />
          {children}
        </>
      ) : null}
    </div>
  )
}
